import * as React from "react";
import type { ComponentProps } from "react";
import { cn } from "app/javascript/utils/index";
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "app/javascript/components/ui/shadcn/alert";

type ShadcnAlertVariant = NonNullable<ComponentProps<typeof Alert>["variant"]>;
type AppAlertVariant = ShadcnAlertVariant | "warning" | "info" | "success";

// 状態ごとの淡い面（design_system.md「状態色は次の組み合わせで使う」の「淡い面」）。
// destructive も shadcn 既定（bg-card・赤字）ではなく状態色の淡い面にする
const brandVariantClassName: Partial<Record<AppAlertVariant, string>> = {
  destructive: "border-tt-destructive-line bg-tt-destructive-subtle text-destructive",
  warning: "border-tt-warning-line bg-tt-warning-subtle text-tt-warning-text",
  info: "border-tt-info-line bg-tt-info-subtle text-tt-info-text",
  success: "border-tt-success-line bg-tt-success-subtle text-tt-success",
};

export type AppAlertProps = Omit<ComponentProps<typeof Alert>, "variant"> & {
  variant?: AppAlertVariant;
};

// 状態（警告・情報・成功・エラー）を伝える囲み（callout）。
// 中身は shadcn の Alert と同じく、直下に svg アイコン（任意）を置き、AppAlertTitle / AppAlertDescription を続ける。
// shadcn の Alert は常に role="alert"（表示した時点で割り込んで読み上げる）のため、既定は role="note"（常にある注記）にする。
// destructive（エラー）は表示したときに読み上げるよう shadcn と同じ role="alert"、
// success は操作の結果を割り込まずに読み上げる role="status" を既定にする。
// warning / info でもすぐに気づいてほしい通知（未読の重要なお知らせなど）は role="alert" を渡す
const defaultRole: Partial<Record<AppAlertVariant, string>> = {
  destructive: "alert",
  success: "status",
};

function AppAlert({ className, variant = "default", role, ...props }: AppAlertProps) {
  return (
    <Alert
      variant={variant === "destructive" ? "destructive" : "default"}
      role={role ?? defaultRole[variant] ?? "note"}
      className={cn("text-body", brandVariantClassName[variant], className)}
      {...props}
    />
  );
}

// 本文の上の小見出し（例: 「重要なお知らせ（未読）」）。shadcn 既定の 1 行での省略（line-clamp-1）をやめ、長いときは折り返す
function AppAlertTitle({ className, ...props }: ComponentProps<typeof AlertTitle>) {
  return <AlertTitle className={cn("line-clamp-none text-caption font-bold tracking-normal", className)} {...props} />;
}

// 本文。shadcn 既定の灰色の文字（text-muted-foreground）をやめ、囲みの文字色を引き継ぐ
function AppAlertDescription({ className, ...props }: ComponentProps<typeof AlertDescription>) {
  return <AlertDescription className={cn("text-body text-current", className)} {...props} />;
}

export { AppAlert, AppAlertTitle, AppAlertDescription };
